import { useEffect, useState } from 'react'
import axios from 'axios'
import { calcularCampos } from '../lib/uploadCampos'

type Row = {
  report: string
  dashboard: string
  month: string
  state: 'CARGADO' | 'PENDIENTE' | 'CERRADO'
  closure_id?: string
}

type Inventory = {
  restaurant: string
  restaurant_id?: string
  rows: Row[]
  counts: { CARGADO: number; PENDIENTE: number; CERRADO: number }
  total: number
}

type RestaurantInfo = {
  id: string
  slug: string
  name: string
  razon_social?: string | null
}

const BADGE: Record<Row['state'], { label: string; cls: string }> = {
  CARGADO: { label: 'Cargado', cls: 'badge-ok' },
  PENDIENTE: { label: 'Pendiente', cls: 'badge-warn' },
  CERRADO: { label: 'Cerrado', cls: 'badge-closed' },
}

// ─── Catálogo de reportes (wiki) ──────────────────────────

type CatalogoEntry = {
  restaurant: string
  dashboard: string
  report: string
  pattern: string
  tipo_informacion: string
  frecuencia: string
  accion: string
  consolida_con?: string | null
  tabla_destino?: string | null
  parser?: string | null
  justificacion?: string | null
  months: number[]
}

type CatalogoResponse = {
  entries: CatalogoEntry[]
  warnings: string[]
  total: number
}

const ACCION_BADGE: Record<string, { label: string; cls: string }> = {
  cargar: { label: 'CARGAR', cls: 'badge-ok' },
  descartar: { label: 'DESCARTAR', cls: 'badge-closed' },
  consolidar: { label: 'CONSOLIDAR', cls: 'badge-info' },
  sin_clasificar: { label: 'SIN CLASIFICAR', cls: 'badge-warn' },
}

const TIPO_LABEL: Record<string, string> = {
  ventas: 'Ventas',
  costos: 'Costos',
  financiero: 'Financiero',
  operaciones: 'Operaciones',
}

const DASH_LABEL: Record<string, string> = {
  financiero: 'Financiero',
  food_cost: 'Food Cost',
  operaciones: 'Operaciones',
  comparativo_diario_ventas: 'Comparativo diario de Ventas',
  comercial_diario_ventas: 'Diario de Ventas',
  ventas_mensuales_producto: 'Ventas Mensuales por Producto',
  oper_tiempos_salon: 'Tiempos en Salón',
  oper_ocupabilidad_mesas: 'Ocupabilidad de Mesas',
  oper_productos_mesero: 'Productos por Mesero',
  oper_rotacion_mesas: 'Rotación por Mesas',
  margen_producto: 'Margen por Producto',
}

const DASHBOARD_TYPES = [
  'financiero',
  'food_cost',
  'comparativo_diario_ventas',
  'comercial_diario_ventas',
  'ventas_mensuales_producto',
  'oper_tiempos_salon',
  'oper_ocupabilidad_mesas',
  'oper_productos_mesero',
  'oper_rotacion_mesas',
  'margen_producto',
] as const
type DashboardType = typeof DASHBOARD_TYPES[number]

// Las listas TIPOS_SIN_MES / TIPOS_CON_ALCANCE viven en lib/uploadCampos.ts,
// junto con la logica que decide que campos se envian. Estaban duplicadas aca y
// se contradecian: un tipo en las DOS listas mandaba alcance=mes sin month.

// Tipos que requieren seleccionar AÑO (el archivo solo trae meses Enero..Diciembre)
const TYPES_REQUIRE_YEAR: ReadonlyArray<DashboardType> = [
  'ventas_mensuales_producto',
]

// Endpoint de carga por tipo (los de tabla dedicada usan endpoint propio)
const UPLOAD_ENDPOINT: Record<string, string> = {
  comparativo_diario_ventas: '/api/admin/upload-comparativo',
  comercial_diario_ventas: '/api/admin/upload-comercial-diario',
  ventas_mensuales_producto: '/api/admin/upload-ventas-mensuales',
  oper_tiempos_salon: '/api/admin/upload-tiempos-salon',
  oper_ocupabilidad_mesas: '/api/admin/upload-ocupabilidad',
  oper_productos_mesero: '/api/admin/upload-productos-mesero',
  oper_rotacion_mesas: '/api/admin/upload-rotacion-mesas',
  financiero: '/api/admin/upload-estado-resultados',
  food_cost: '/api/admin/upload-food-cost',
  margen_producto: '/api/admin/upload-margen-producto',
}

// ─── Leyenda de formato esperado por reporte (fuente: routers de carga) ───
type FormatInfo = {
  archivo: string
  separador: string
  columnas: string[]
  notas?: string[]
}

const UPLOAD_FORMAT_LEGEND: Record<DashboardType, FormatInfo> = {
  financiero: {
    archivo: 'XLSX (hoja "E.R.")',
    separador: 'celdas (Excel)',
    columnas: [
      'Fila 5: encabezados de periodo (Noviembre 2025, Diciembre 2025, Ene 2025, Ene 2026, … Junio 2026, Julio–Dic 2026, TOTAL 2025, TOTAL 2026)',
      'Col A: código + nombre de cuenta (ej. "1000 VENTAS NETAS", "7012110 Mercaderías - Alimentos")',
      'Cada periodo trae 2 columnas: valor y % sobre ventas',
    ],
    notas: [
      'El archivo trae VARIOS periodos (Nov–Dic 2025, Ene–Jul 2026, TOTAL 2025/2026 y Ago–Dic 2026 de plantilla).',
      'Alcance "Toda la historia" = reemplaza 2025 + 2026 completos (los periodos que el archivo no traiga se pierden: se avisa en la respuesta).',
      'Alcance "Solo el mes indicado" = carga/reemplaza SOLO ese mes (ej. Julio 2026) sin tocar el resto.',
      'TOTAL 2025 / TOTAL 2026 = subtotales del semestre H1 (Ene–Jun).',
      'Ago–Dic 2026 = proyección; se cargan con bandera es_proyeccion.',
      'Mismas cuentas que aparecen en varias secciones se discriminan por sección.',
    ],
  },
  food_cost: {
    archivo: 'XLSX (hoja "Foot Cost")',
    separador: 'celdas (Excel)',
    columnas: [
      'Bloques mensuales "A <ENERO|FEBRERO|...> 2026" (Ene–Jun con datos; Jul–Dic en 0).',
      'Resumen por mes: Inventario Inicial, Compras, Inventario Final, Costo, Comida del personal, Costo de ventas neto.',
      'Desglose: 19 categorías de insumo (Alimentos, Bebidas, Licores, Abarrotes, etc.)',
    ],
    notas: [
      'Solo se cargan los meses Ene–Jun reales (Jul–Dic en 0 son plantilla, no se toman).',
      'El alcance manda: "Solo el mes indicado" carga únicamente ese mes; "Toda la historia" reemplaza todos los meses que traiga el archivo.',
      'Costo neto H1 del Food Cost debe cuadrar con la línea COSTO DE VENTAS del P&L.',
    ],
  },
  comparativo_diario_ventas: {
    archivo: 'CSV',
    separador: 'punto y coma ( ; )',
    columnas: [
      'MM', 'DD', 'D_ANT', 'D_ACT', 'VR_VENTAS_2026', 'VR_VENTAS_2025',
      'Vr_VARIACION', 'Q_2026', 'Q_2025', 'Q_VARIACION',
    ],
    notas: [
      'Números: miles con coma, decimal con punto (ej. "25,478.00").',
      'El mes se detecta de la columna MM (todas las filas del mismo mes).',
      'El año es fijo 2026 (viene del nombre de columna VR_VENTAS_2026).',
    ],
  },
  comercial_diario_ventas: {
    archivo: 'CSV',
    separador: 'punto y coma ( ; ) o tab',
    columnas: [
      'Formato Alado: YYYY;MM;DD;Dia;Pedidos;Pax;En el Local;Delivery;Take Out;Market Empresas;Retail;Venta;Parti_Dia',
      'Formato Cosme: anio;mm;dd;dia;pedidos;pax;en_el_local;delivery;take_out;PANGUCH;RAPPI;venta;parti_di',
      'Formato La 73: Fecha;Dia;Pedidos;Pax;En el Local;Delivery;PICK UP;Rappi;JUSTO;Venta;% Total',
    ],
    notas: [
      'Números: miles con coma, decimal con punto.',
      'El periodo se detecta de las columnas YYYY/MM o Fecha (un solo periodo por archivo).',
      'Los canales cambian por restaurante: se mapean automáticamente según el slug (alado/cosme/la73).',
      '"Market Empresas" es UNA sola columna (Alado).',
    ],
  },
  ventas_mensuales_producto: {
    archivo: 'CSV ( ; , o tab) o XLS/XLSX',
    separador: 'punto y coma, coma o tab (CSV)',
    columnas: [
      'restaurante', 'Canal', 'Producto', 'Enero', 'Febrero', 'Marzo', 'Abril',
      'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
    ],
    notas: [
      'El año se indica en el formulario (el archivo solo trae meses).',
      'Números: "73975.20", "73,975.20" (miles) o europeo "73.975,20".',
      'Meses con valor 0 no generan registros.',
    ],
  },
  oper_tiempos_salon: {
    archivo: 'XLS (Crystal Reports) o XLSX',
    separador: 'celdas (Excel)',
    columnas: [
      'Pedido', 'Salon', 'Mesa', 'Adulto', 'Registro (T1)', 'Documento',
      'Fecha (T2)', 'Pago (T3)', '(T2-T1)', '(T3-T1)',
    ],
    notas: [
      'El periodo se detecta de la columna T1 Registro (DD/MM/YYYY HH:MM:SS).',
      'Se importan solo las filas donde "Pedido" es un número (ignora metadatos y sub-encabezados "Fecha:").',
      'T2-T1 y T3-T1 son minutos ya calculados por el archivo.',
    ],
  },
  oper_ocupabilidad_mesas: {
    archivo: 'XLS (Crystal Reports) o XLSX',
    separador: 'celdas (Excel)',
    columnas: [
      'Hora', 'Mesas', 'Adicional', 'Total', '% Mesas', 'Adultos', '% Adultos', 'Niños', 'Venta',
    ],
    notas: [
      'El periodo (año y mes) se detecta del encabezado "Del 01/Ene/2026 ... Al ...".',
      'Una fila por franja de 30 min (12:30 → 23:30).',
      'Del pie del reporte también se cargan: mesas disponibles, días abiertos y sillas (tabla oper_mesas_disponible).',
    ],
  },
  oper_productos_mesero: {
    archivo: 'XLS (Crystal Reports) o XLSX',
    separador: 'celdas (Excel)',
    columnas: [
      'Mesero', 'Grupo', 'SubGrupo', 'Producto', 'Tipo Servicio', 'Cantidad', 'Venta',
    ],
    notas: [
      'El periodo (año y mes) se detecta del encabezado "Del 01/Ene/2026 ... Al ...".',
      'Reporte agrupado por mesero; cada bloque trae Comensales y Pedidos (tabla oper_mesero_resumen).',
      'Ticket promedio y consumo por comensal se calculan, no se cargan.',
    ],
  },
  oper_rotacion_mesas: {
    archivo: 'XLS (Crystal Reports) o XLSX',
    separador: 'celdas (Excel)',
    columnas: [
      'Secciones por zona: "Principal Salon", "Principal Mezzanine", "Principal Privado", "Principal Terraza" (col A)',
      'Mesa (col C): 1, 10, 10.1, B1, M1, T1, ...',
      'Cantidad Asientos (col H)',
    ],
    notas: [
      'El periodo (año y mes) se detecta del encabezado "Del 01/Ene/2026 ... Al ...".',
      'Una fila por mesa por periodo (zona, mesa, asientos). Los "Total" de zona son subtotales y se ignoran.',
      'Alimenta el heatmap "Rotación de mesas" = comensales(ocupabilidad) ÷ asientos del inventario.',
    ],
  },
  margen_producto: {
    archivo: 'XLS (Crystal Reports) o XLSX',
    separador: 'celdas (Excel)',
    columnas: [
      'Grupo / Categoria (ej. "BAR / Aguas y Gaseosas", "Restaurante / Fondos")',
      'Producto', 'Val.Venta Unitario', 'Insumos/Gastos/Mano de Obra unitarios y totales',
      'Cantidad', 'Venta Total', 'Utilidad Total (Facturada)',
    ],
    notas: [
      'El periodo (año y mes) se detecta del encabezado "Del 01/Ene/2026 Al 31/Ene/2026".',
      'Se carga 1 fila por producto con su margen detallado; la jerarquía define grupo/categoría.',
      'La Utilidad Total (col 42 del reporte) alimenta el ranking top5/bottom5 del dashboard financiero.',
      'Identidad validada: Venta Total − Costo Total = Utilidad Total (al céntimo).',
      'El nombre del archivo puede ser "Utilidad Operativa" o "Margen por producto" (ambos formatos INFOREST iguales).',
    ],
  },
}

function periodKey(row: Row): string {
  return `${row.dashboard}|${row.month}`
}

type Tab = 'inventory' | 'catalogo' | 'upload' | 'indicadores'

export default function Admin() {
  const [tab, setTab] = useState<Tab>('inventory')
  return (
    <section className="admin">
      <h2>Administrador</h2>
      <div className="admin-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'inventory'}
          className={`tab ${tab === 'inventory' ? 'active' : ''}`}
          onClick={() => setTab('inventory')}
        >
          📋 Inventario
        </button>
        <button
          role="tab"
          aria-selected={tab === 'catalogo'}
          className={`tab ${tab === 'catalogo' ? 'active' : ''}`}
          onClick={() => setTab('catalogo')}
        >
          📚 Catálogo de reportes
        </button>
        <button
          role="tab"
          aria-selected={tab === 'upload'}
          className={`tab ${tab === 'upload' ? 'active' : ''}`}
          onClick={() => setTab('upload')}
        >
          📤 Carga de reportes
        </button>
        <button
          role="tab"
          aria-selected={tab === 'indicadores'}
          className={`tab ${tab === 'indicadores' ? 'active' : ''}`}
          onClick={() => setTab('indicadores')}
        >
          📊 Indicadores (KPIs)
        </button>
      </div>
      {tab === 'inventory' ? <InventoryTab /> :
       tab === 'catalogo' ? <CatalogoTab /> :
       tab === 'indicadores' ? <IndicadoresTab /> :
       <UploadTab />}
    </section>
  )
}

// ─── Tab: Catálogo de reportes (wiki) ───────────────────

function CatalogoTab() {
  const [data, setData] = useState<CatalogoResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filterRest, setFilterRest] = useState<string>('all')
  const [filterTipo, setFilterTipo] = useState<string>('all')
  const [filterAccion, setFilterAccion] = useState<string>('all')
  const [query, setQuery] = useState<string>('')

  async function load() {
    try {
      const res = await axios.get('/api/admin/catalogo')
      setData(res.data)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message)
    }
  }

  useEffect(() => { load() }, [])

  if (error) return <p className="error">{error}</p>
  if (!data) return <p>Cargando catálogo…</p>

  const restaurants = Array.from(new Set(data.entries.map((e) => e.restaurant))).sort()
  const tipos = Array.from(new Set(data.entries.map((e) => e.tipo_informacion))).sort()

  const rows = data.entries.filter((e) => {
    const okRest = filterRest === 'all' || e.restaurant === filterRest
    const okTipo = filterTipo === 'all' || e.tipo_informacion === filterTipo
    const okAccion = filterAccion === 'all' || e.accion === filterAccion
    const q = query.trim().toLowerCase()
    const okQ = !q || e.report.toLowerCase().includes(q) || e.pattern.toLowerCase().includes(q)
    return okRest && okTipo && okAccion && okQ
  })

  return (
    <>
      <p className="muted">
        Guía de qué hacer con cada reporte recibido. <strong>CARGAR</strong> = carga directa ·{' '}
        <strong>CONSOLIDAR</strong> = ya cubierto por otro reporte · <strong>DESCARTAR</strong> = no se consume.
      </p>

      <div className="admin-filters">
        <select value={filterRest} onChange={(e) => setFilterRest(e.target.value)} aria-label="Filtrar por restaurante">
          <option value="all">Todos los restaurantes</option>
          {restaurants.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} aria-label="Filtrar por tipo">
          <option value="all">Todos los tipos</option>
          {tipos.map((t) => <option key={t} value={t}>{TIPO_LABEL[t] ?? t}</option>)}
        </select>
        <select value={filterAccion} onChange={(e) => setFilterAccion(e.target.value)} aria-label="Filtrar por acción">
          <option value="all">Todas las acciones</option>
          <option value="cargar">CARGAR</option>
          <option value="consolidar">CONSOLIDAR</option>
          <option value="descartar">DESCARTAR</option>
          <option value="sin_clasificar">SIN CLASIFICAR</option>
        </select>
        <input
          type="search"
          placeholder="Buscar reporte o patrón…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Buscar reporte"
        />
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Reporte</th>
            <th>Restaurante</th>
            <th>Tipo</th>
            <th>Acción</th>
            <th>Destino / Consolida con</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e, i) => {
            const badge = ACCION_BADGE[e.accion] ?? ACCION_BADGE.sin_clasificar
            const destino =
              e.accion === 'consolidar'
                ? `→ ${e.consolida_con ?? '?'}`
                : e.tabla_destino ?? (e.accion === 'descartar' ? '—' : '—')
            return (
              <tr key={`${e.restaurant}-${e.report}-${i}`}>
                <td>
                  <div>{e.report}</div>
                  <div className="muted" style={{ fontSize: '0.7rem' }}>{e.pattern}</div>
                  {e.justificacion && (
                    <div className="muted" style={{ fontSize: '0.7rem', maxWidth: 320 }}>💡 {e.justificacion}</div>
                  )}
                </td>
                <td>{e.restaurant}</td>
                <td>{TIPO_LABEL[e.tipo_informacion] ?? e.tipo_informacion}</td>
                <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                <td>{destino}</td>
              </tr>
            )
          })}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="empty">Sin resultados con el filtro actual</td></tr>
          )}
        </tbody>
      </table>

      {data.warnings.length > 0 && (
        <details className="admin-note">
          <summary>⚠️ {data.warnings.length} advertencia(s) de integridad</summary>
          <ul>{data.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </details>
      )}

      <p className="admin-note">Total de reportes catalogados: {data.total}</p>
    </>
  )
}

// ─── Tab: Inventario (lógica actual) ───────────────────

function InventoryTab() {
  const [data, setData] = useState<Inventory | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filterDash, setFilterDash] = useState<string>('all')
  const [filterState, setFilterState] = useState<string>('all')
  const [busy, setBusy] = useState<string | null>(null)

  async function load() {
    try {
      const res = await axios.get('/api/admin/inventory', { params: { restaurant: 'alado' } })
      setData(res.data)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message)
    }
  }

  useEffect(() => { load() }, [])

  async function closePeriod(row: Row) {
    const label = `${DASH_LABEL[row.dashboard] ?? row.dashboard} ${row.month}`
    if (!window.confirm(`¿Cerrar el periodo ${label}? Ya no se podrá recargar sin --force.`)) return
    setBusy(periodKey(row))
    try {
      await axios.post('/api/admin/period-closure', {
        restaurant_id: data!.restaurant_id,
        dashboard_type: row.dashboard,
        month: `${row.month}-01`,
        note: 'Cerrado desde la vista Administrador',
      })
      await load()
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message)
    } finally {
      setBusy(null)
    }
  }

  async function reopenPeriod(row: Row) {
    const label = `${DASH_LABEL[row.dashboard] ?? row.dashboard} ${row.month}`
    if (!window.confirm(`¿Reabrir el periodo ${label}?`)) return
    setBusy(periodKey(row))
    try {
      await axios.delete(`/api/admin/period-closure/${row.closure_id}`)
      await load()
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message)
    } finally {
      setBusy(null)
    }
  }

  if (error) return <p className="error">{error}</p>
  if (!data) return <p>Cargando inventario…</p>

  const rows = data.rows.filter((r) => {
    const okDash = filterDash === 'all' || r.dashboard === filterDash
    const okState = filterState === 'all' || r.state === filterState
    return okDash && okState
  })

  const acted = new Set<string>()
  const actionByPeriod = new Map<string, Row>()
  for (const r of rows) {
    const k = periodKey(r)
    if (!acted.has(k)) {
      acted.add(k)
      actionByPeriod.set(k, r)
    }
  }

  return (
    <>
      <div className="admin-kpis">
        <div className="kpi"><span className="kpi-num ok">{data.counts.CARGADO}</span><span>Cargados</span></div>
        <div className="kpi"><span className="kpi-num warn">{data.counts.PENDIENTE}</span><span>Pendientes</span></div>
        <div className="kpi"><span className="kpi-num closed">{data.counts.CERRADO}</span><span>Cerrados</span></div>
        <div className="kpi"><span className="kpi-num">{data.total}</span><span>Total celdas</span></div>
      </div>

      <div className="admin-filters">
        <select value={filterDash} onChange={(e) => setFilterDash(e.target.value)} aria-label="Filtrar por dashboard">
          <option value="all">Todos los dashboards</option>
          <option value="comercial">Comercial</option>
          <option value="financiero">Financiero</option>
          <option value="operaciones">Operaciones</option>
        </select>
        <select value={filterState} onChange={(e) => setFilterState(e.target.value)} aria-label="Filtrar por estado">
          <option value="all">Todos los estados</option>
          <option value="CARGADO">Cargados</option>
          <option value="PENDIENTE">Pendientes</option>
          <option value="CERRADO">Cerrados</option>
        </select>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Reporte</th>
            <th>Dashboard</th>
            <th>Mes</th>
            <th>Estado</th>
            <th>Acción</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const k = periodKey(r)
            const isActionRow = actionByPeriod.get(k)?.report === r.report
            const isBusy = busy === k
            return (
              <tr key={`${r.dashboard}-${r.month}-${r.report}-${i}`}>
                <td>{r.report}</td>
                <td>{DASH_LABEL[r.dashboard] ?? r.dashboard}</td>
                <td>{r.month}</td>
                <td><span className={`badge ${BADGE[r.state].cls}`}>{BADGE[r.state].label}</span></td>
                <td>
                  {isActionRow && r.state === 'CERRADO' && (
                    <button className="btn btn-reopen" disabled={isBusy} onClick={() => reopenPeriod(r)}>
                      {isBusy ? '…' : 'Reabrir'}
                    </button>
                  )}
                  {isActionRow && r.state !== 'CERRADO' && (
                    <button className="btn btn-close" disabled={isBusy} onClick={() => closePeriod(r)}>
                      {isBusy ? '…' : 'Cerrar periodo'}
                    </button>
                  )}
                  {!isActionRow && <span className="muted">—</span>}
                </td>
              </tr>
            )
          })}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="empty">Sin resultados con el filtro actual</td></tr>
          )}
        </tbody>
      </table>

      <p className="admin-note">Cerrar un periodo bloquea re-cargas desde la ingesta (CLI) para ese mes. Usa Reabrir si necesitas volver a cargarlo.</p>
    </>
  )
}

// Shape del 409: el endpoint genérico manda lista de ids; los de tabla
// dedicada (comparativo, comercial diario) mandan conteo de filas.
type ConflictDetail = {
  message?: string
  existing_upload_ids?: string[]
  existing_count?: number
}

type UploadResult = {
  upload_id: string
  status: string
  row_count: number
  mesas_disponibles_row?: boolean
  errors: string[]
  warnings: string[]
  saved_to: string
  log_file: string
}

function UploadTab() {
  const [restaurants, setRestaurants] = useState<RestaurantInfo[]>([])
  const [restaurantId, setRestaurantId] = useState<string>('')
  const [dashboardType, setDashboardType] = useState<DashboardType>('financiero')
  const [month, setMonth] = useState<string>('')
  const [anio, setAnio] = useState<string>('2026')
  // Alcance de la carga (solo para tipos de TYPES_WITH_ALCANCE, ej. P&L financiero)
  const [alcanceCarga, setAlcanceCarga] = useState<'historia' | 'mes'>('historia')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState<boolean>(false)
  const [result, setResult] = useState<UploadResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingConflict, setPendingConflict] = useState<ConflictDetail | null>(null)

  // ── Alcance de la carga (solo reportes con varios periodos en el archivo) ──
  // La decision vive en lib/uploadCampos.ts (logica pura, con tests): hay tipos
  // que estan en TIPOS_SIN_MES y en TIPOS_CON_ALCANCE a la vez y el calculo
  // inline se contradecia.
  const { alcanceActivo, modoMes, mesDesdeArchivo, requiereMes, enviaMonth, enviaAlcance } =
    calcularCampos(dashboardType, alcanceCarga)

  useEffect(() => {
    axios.get('/api/restaurants/')
      .then((r) => setRestaurants(r.data.restaurants ?? []))
      .catch((e) => setError(`Error al cargar restaurants: ${e.message}`))
    // Default mes: mes actual en formato YYYY-MM
    const now = new Date()
    setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`)
    setAnio(String(now.getFullYear()))
  }, [])

  async function execute(force = false) {
    // LOG TEMPORAL (quitar cuando el flujo esté estable)
    console.log('[upload] ▶ execute inicio', {
      tipo: dashboardType,
      restaurantId,
      month,
      force,
      file: file ? { name: file.name, size: file.size, type: file.type } : null,
    })
    if (!file || !restaurantId) {
      setError('Completa restaurante y archivo antes de procesar')
      console.warn('[upload] validación local falló: falta file o restaurantId')
      return
    }
    if (requiereMes && !month) {
      setError('Selecciona el mes')
      console.warn('[upload] validación local falló: falta mes')
      return
    }
    if (TYPES_REQUIRE_YEAR.includes(dashboardType) && !anio) {
      setError('Selecciona el año')
      console.warn('[upload] validación local falló: falta año')
      return
    }
    setBusy(true)
    setError(null)
    setResult(null)
    setPendingConflict(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('restaurant_id', restaurantId)
      // Solo enviar month si el tipo lo requiere. La invariante (alcance='mes'
      // implica month) la garantiza calcularCampos, y esta cubierta por tests.
      if (enviaMonth) {
        fd.append('month', month)
      }
      // Alcance de la carga (historia completa vs un solo mes)
      if (enviaAlcance) {
        fd.append('alcance', alcanceCarga)
      }
      // Enviar anio si el tipo lo requiere (ventas_mensuales_producto)
      if (TYPES_REQUIRE_YEAR.includes(dashboardType)) {
        fd.append('anio', anio)
      }
      // Elegir endpoint según el tipo
      const url = UPLOAD_ENDPOINT[dashboardType] ?? '/api/admin/upload'
      const finalUrl = force ? `${url}?force=true` : url
      console.log('[upload] POST →', finalUrl)
      const res = await axios.post(finalUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      console.log('[upload] ✅ respuesta 200', {
        status: res.status,
        data: res.data,
      })
      setResult(res.data)
    } catch (err: any) {
      console.error('[upload] ❌ error', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
        err,
      })
      const detail = err.response?.data?.detail
      if (err.response?.status === 409 && detail && typeof detail === 'object') {
        // LOG: shape del detail del 409 (para depurar conflictos de schema)
        console.log('[upload] 409 detail keys:', Object.keys(detail), detail)
        setPendingConflict(detail)
      } else {
        setError(typeof detail === 'string' ? detail : err.message)
      }
    } finally {
      console.log('[upload] fin (busy=false)')
      setBusy(false)
    }
  }

  const fmt = UPLOAD_FORMAT_LEGEND[dashboardType]

  return (
    <div className="upload-tab">
      <h3>Cargar reporte mensual</h3>
      <p className="muted">
        Sube un archivo CSV o XLSX con el reporte del mes. Si el mes ya tiene datos cargados,
        se te preguntará si deseas reemplazarlos. Si el periodo está cerrado, necesitas
        marcar la opción "force" para reabrir.
      </p>

      <div className="upload-layout">
        {/* Columna izquierda: formulario */}
        <div className="upload-form">
          <div className="form-row">
            <label>
              Restaurante
              <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}>
                <option value="">Seleccionar…</option>
                {restaurants.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}{r.razon_social ? ` — ${r.razon_social}` : ''} ({r.slug})
                  </option>
                ))}
              </select>
            </label>

            <label>
              Tipo de información
              <select value={dashboardType} onChange={(e) => setDashboardType(e.target.value as DashboardType)}>
                {DASHBOARD_TYPES.map((d) => (
                  <option key={d} value={d}>{DASH_LABEL[d] ?? d}</option>
                ))}
              </select>
              {mesDesdeArchivo && (
                <span className="muted">ℹ️ El mes se detecta del archivo.</span>
              )}
            </label>
          </div>

          <div className="form-row">
            {requiereMes && (
              <label>
                Mes (YYYY-MM)
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
                {alcanceActivo && (
                  <span className="muted">
                    ℹ️ El mes a cargar/reemplazar (ej. 2026-07 = Julio 2026).
                  </span>
                )}
              </label>
            )}

            {alcanceActivo && (
              <label>
                Alcance de la carga
                <select
                  value={alcanceCarga}
                  onChange={(e) => setAlcanceCarga(e.target.value as 'historia' | 'mes')}
                >
                  <option value="historia">Toda la historia del archivo</option>
                  <option value="mes">Solo el mes indicado</option>
                </select>
                <span className="muted">
                  {modoMes
                    ? 'ℹ️ Carga/reemplaza SOLO ese mes; el resto de la tabla no se toca.'
                    : anio && TYPES_REQUIRE_YEAR.includes(dashboardType)
                      ? `ℹ️ Reemplaza TODOS los meses que trae el archivo para ${anio}.`
                      : 'ℹ️ Reemplaza TODOS los periodos que trae el archivo.'}
                </span>
              </label>
            )}

            {TYPES_REQUIRE_YEAR.includes(dashboardType) && (
              <label>
                Año (YYYY)
                <input type="number" min={2000} max={2100} value={anio} onChange={(e) => setAnio(e.target.value)} />
                <span className="muted">ℹ️ El archivo solo trae meses; el año se indica aquí.</span>
              </label>
            )}
          </div>

          <label className="file-input">
            Archivo (CSV, XLS o XLSX)
            <input
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && <span className="muted">{file.name} ({Math.round(file.size / 1024)} KB)</span>}
          </label>

          <div className="upload-actions">
            <button className="btn btn-primary" disabled={busy || !file || !restaurantId} onClick={() => execute(false)}>
              {busy ? 'Procesando…' : 'Ejecutar Procesamiento'}
            </button>
          </div>

          {error && <p className="error">❌ {error}</p>}
        </div>

        {/* Columna derecha: leyenda de formato + feedback */}
        <div className="upload-side">
          <div className="upload-legend">
            <h4>📄 Formato esperado — {DASH_LABEL[dashboardType] ?? dashboardType}</h4>
            <p><strong>Archivo:</strong> {fmt.archivo}</p>
            <p><strong>Separador:</strong> {fmt.separador}</p>
            <p><strong>Columnas (en orden):</strong></p>
            <p className="legend-cols"><code>{fmt.columnas.join(' · ')}</code></p>
            {fmt.notas && fmt.notas.length > 0 && (
              <ul className="legend-notes">
                {fmt.notas.map((n, i) => <li key={i}>ℹ️ {n}</li>)}
              </ul>
            )}
          </div>

          {pendingConflict && (
            <div className="upload-conflict">
              <p>⚠️ {pendingConflict.message ?? 'El periodo ya tiene datos cargados.'}</p>
              {pendingConflict.existing_upload_ids?.length ? (
                <p className="muted">Uploads previos: {pendingConflict.existing_upload_ids.join(', ')}</p>
              ) : pendingConflict.existing_count != null ? (
                <p className="muted">Filas existentes en el periodo: {pendingConflict.existing_count}</p>
              ) : null}
              {alcanceActivo && (
                <p className="muted">
                  {modoMes
                    ? `Se reemplazará SOLO el mes ${month || '(sin indicar)'}.`
                    : anio && TYPES_REQUIRE_YEAR.includes(dashboardType)
                      ? `Se reemplazarán TODOS los meses que trae el archivo para ${anio}.`
                      : 'Se reemplazarán TODOS los periodos que trae el archivo.'}
                </p>
              )}
              <div className="upload-actions">
                <button className="btn btn-danger" disabled={busy} onClick={() => execute(true)}>
                  {busy
                    ? 'Reemplazando…'
                    : alcanceActivo
                      ? modoMes
                        ? `Sí, reemplazar solo ${month}`
                        : 'Sí, reemplazar toda la historia'
                      : 'Sí, reemplazar'}
                </button>
                <button className="btn" disabled={busy} onClick={() => setPendingConflict(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {result && (
            <div className="upload-result ok">
              <h4>✅ Procesado correctamente</h4>
              <ul>
                <li><strong>Upload ID:</strong> <code>{result.upload_id}</code></li>
                <li><strong>Status:</strong> {result.status}</li>
                <li><strong>Filas insertadas:</strong> {result.row_count}</li>
                {result.mesas_disponibles_row != null && (
                  <li><strong>Mesas disponibles:</strong> {result.mesas_disponibles_row ? 'cargadas ✓' : 'no'}</li>
                )}
                <li><strong>Errores:</strong> {result.errors.length}</li>
                <li><strong>Warnings:</strong> {result.warnings.length}</li>
                <li><strong>Archivo guardado:</strong> <code>{result.saved_to}</code></li>
                <li><strong>Log:</strong> <code>{result.log_file}</code></li>
              </ul>
              {result.errors.length > 0 && (
                <details><summary>Ver errores</summary>
                  <pre>{result.errors.join('\n')}</pre>
                </details>
              )}
              {result.warnings.length > 0 && (
                <div className="upload-conflict">
                  {result.warnings.map((w, i) => <p key={i}>⚠️ {w}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Tab: Indicadores (KPIs) ────────────────────────────

type Indicador = {
  kpi_id: string
  modulo: string
  categoria: string
  indicador: string
  que_mide: string
  inputs?: string | null
  formula?: string | null
  unidad?: string | null
  frecuencia?: string | null
  datos_disponibles: string
  archivo_fuente?: string | null
  en_tablero: boolean
  por_restaurante?: Record<string, boolean>
  estado: 'CALCULADO' | 'DATOS_OK' | 'SIN_DATOS'
  tabla_fuente?: string | null
}

type IndicadoresResponse = {
  modulo?: string | null
  restaurant?: string | null
  total: number
  calculados: number
  por_estado: Record<string, number>
  rows: Indicador[]
}

const ESTADO_BADGE: Record<Indicador['estado'], { label: string; cls: string }> = {
  CALCULADO: { label: '✓ Calculado', cls: 'badge-ok' },
  DATOS_OK: { label: 'Datos OK (sin cargar)', cls: 'badge-info' },
  SIN_DATOS: { label: 'Sin datos', cls: 'badge-closed' },
}

// Etiquetas amigables de restaurante para el check por restaurante
const REST_LABEL: Record<string, string> = {
  alado: 'Alado',
  cosme: 'Cosme',
  la73: 'La 73',
}

// Slugs de restaurante ordenados para las columnas (viene de los datos si existe)
const REST_SLUGS = ['alado', 'cosme', 'la73']

function IndicadoresTab() {
  const [data, setData] = useState<IndicadoresResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filterModulo, setFilterModulo] = useState<string>('all')
  const [filterEstado, setFilterEstado] = useState<string>('all')
  const [query, setQuery] = useState<string>('')
  const [selected, setSelected] = useState<Indicador | null>(null)

  async function load() {
    try {
      const params: any = {}
      if (filterModulo !== 'all') params.modulo = filterModulo
      const res = await axios.get('/api/admin/indicadores', { params })
      setData(res.data)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message)
    }
  }

  useEffect(() => { load() }, [filterModulo])

  if (error) return <p className="error">{error}</p>
  if (!data) return <p>Cargando indicadores…</p>

  // Slugs de restaurante reales (del primer KPI con datos, o fallback fijo)
  const restSlugs = data.rows[0]?.por_restaurante ? Object.keys(data.rows[0].por_restaurante) : REST_SLUGS

  const rows = data.rows.filter((k) => {
    const okEstado = filterEstado === 'all' || k.estado === filterEstado
    const q = query.trim().toLowerCase()
    const okQuery = !q ||
      k.indicador.toLowerCase().includes(q) ||
      k.kpi_id.toLowerCase().includes(q) ||
      k.categoria.toLowerCase().includes(q)
    return okEstado && okQuery
  })

  return (
    <div className="indicadores">
      <h3>Catálogo de Indicadores (KPIs)</h3>
      <p className="muted">
        Maestro de KPIs del grupo (fuente: Catalogo_Indicadores_GrupoNGH).{' '}
        Estado derivado del cruce del catálogo con los datos cargados.
      </p>

      <div className="kpi-summary">
        <span><strong>{data.calculados}</strong> calculados</span>
        <span><strong>{data.por_estado['DATOS_OK'] ?? 0}</strong> datos OK pendientes</span>
        <span><strong>{data.por_estado['SIN_DATOS'] ?? 0}</strong> sin datos</span>
        <span><strong>{data.total}</strong> total</span>
      </div>

      <div className="filters">
        <label>
          Módulo
          <select value={filterModulo} onChange={(e) => setFilterModulo(e.target.value)}>
            <option value="all">Todos</option>
            <option value="financiero">Financiero</option>
            <option value="operaciones_rrhh">Operaciones y RRHH</option>
            <option value="comercial">Comercial</option>
          </select>
        </label>
        <label>
          Estado
          <select value={filterEstado} onChange={(e) => setFilterEstado(e.target.value)}>
            <option value="all">Todos</option>
            <option value="CALCULADO">Calculado</option>
            <option value="DATOS_OK">Datos OK</option>
            <option value="SIN_DATOS">Sin datos</option>
          </select>
        </label>
        <label>
          Buscar
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="kpi, indicador, categoría…" />
        </label>
      </div>

      <table className="table kpi-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Indicador</th>
            <th>Categoría</th>
            <th>Frecuencia</th>
            <th>Estado</th>
            {restSlugs.map((slug) => (
              <th key={slug} className="col-rest">{REST_LABEL[slug] ?? slug}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((k) => (
            <tr key={k.kpi_id} onClick={() => setSelected(k)} className="clickable">
              <td><code>{k.kpi_id}</code></td>
              <td>{k.indicador}</td>
              <td>{k.categoria}</td>
              <td>{k.frecuencia ?? '—'}</td>
              <td><span className={`badge ${ESTADO_BADGE[k.estado].cls}`}>{ESTADO_BADGE[k.estado].label}</span></td>
              {restSlugs.map((slug) => (
                <td key={slug} className="col-rest">{k.por_restaurante?.[slug] ? "✅" : ""}</td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4 + restSlugs.length} className="muted">Sin resultados</td></tr>
          )}
        </tbody>
      </table>

      {selected && (
        <div className="kpi-detail">
          <h4>{selected.kpi_id} — {selected.indicador}</h4>
          <p><strong>Qué mide:</strong> {selected.que_mide}</p>
          {selected.formula && <p><strong>Fórmula:</strong> <code>{selected.formula}</code></p>}
          <p><strong>Unidad:</strong> {selected.unidad ?? '—'} · <strong>Frecuencia:</strong> {selected.frecuencia ?? '—'}</p>
          <p>
            <strong>Estado:</strong> <span className={`badge ${ESTADO_BADGE[selected.estado].cls}`}>{ESTADO_BADGE[selected.estado].label}</span>
            {selected.en_tablero && <span className="muted"> · 📊 en tablero</span>}
            {selected.tabla_fuente && <span className="muted"> · fuente: <code>{selected.tabla_fuente}</code></span>}
            {selected.archivo_fuente && <span className="muted"> · reporte: <code>{selected.archivo_fuente}</code></span>}
          </p>
          <button className="btn btn-link" onClick={() => setSelected(null)}>Cerrar</button>
        </div>
      )}
    </div>
  )
}
